<template>
  <div
    ref="phoneDropdownRef"
    class="position-relative"
  >
    <!-- Trigger Input Box -->
    <div
      class="form-control d-flex align-items-center justify-content-between cursor-pointer"
      style="min-height: 38px;"
      @click="isDropdownOpen = !isDropdownOpen"
    >
      <span
        v-if="value"
        class="text-sm text-[#333333] font-medium"
      >
        {{ formatDisplayPhone(value) }}
      </span>
      <span
        v-else
        class="text-sm text-gray-400"
      >
        Pilih nomor untuk CS AI
      </span>
      <b-icon
        :icon="isDropdownOpen ? 'chevron-up' : 'chevron-down'"
        class="text-gray-500"
      />
    </div>

    <!-- Dropdown Content Menu -->
    <div
      v-show="isDropdownOpen"
      class="position-absolute w-100 bg-white border rounded shadow-sm py-1 mt-0.5"
      style="z-index: 1000; border-color: #E2E8F0; max-height: 260px; overflow-y: auto;"
    >
      <!-- Existing Numbers List -->
      <div v-if="numberOptions.length > 0">
        <div
          v-for="(item, index) in numberOptions"
          :key="item.id || item.phone_number || index"
          class="d-flex align-items-center justify-content-between px-1 py-0.5 cursor-pointer hover:bg-gray-50 transition-colors"
          :class="{ 'bg-[#FFF7ED]': isSelected(item) }"
          @click="onSelectPhoneNumber(item)"
        >
          <!-- Phone Number Text -->
          <span class="text-sm text-[#333333] font-medium">
            {{ formatDisplayPhone(item.phone_number || item) }}
          </span>

          <!-- Right: Checkmark & Delete Icon -->
          <div class="d-flex align-items-center gap-2">
            <b-icon-check
              v-if="isSelected(item)"
              class="text-primary font-bold mr-1"
            />
            <button
              type="button"
              class="p-0 border-0 bg-transparent text-[#E31A1A] hover:text-red-700 cursor-pointer focus:outline-none d-flex align-items-center justify-content-center"
              title="Hapus Nomor"
              @click.stop="openDeleteModal(item)"
            >
              <b-icon-trash class="text-danger" />
            </button>
          </div>
        </div>
      </div>

      <div
        v-else
        class="px-1 py-0.5 text-xs text-muted text-center"
      >
        Tidak ada nomor tersedia
      </div>

      <!-- Bottom Add Number Row -->
      <!-- State 1: "+ Tambah Nomor" Link -->
      <div
        v-if="canAddNumber && !isAddingNumber"
        class="pt-1 px-1 border-t border-gray-100 mt-0.5"
      >
        <button
          type="button"
          class="text-primary font-medium text-sm p-0 border-0 bg-transparent d-flex align-items-center gap-1 cursor-pointer hover:underline focus:outline-none w-100"
          @click.stop="openAddNumber"
        >
          <b-icon-plus class="text-primary" />
          Tambah Nomor
        </button>
      </div>

      <!-- State 2: Inline Input Form -->
      <div
        v-else-if="canAddNumber && isAddingNumber"
        class="pt-1 px-1 border-t border-gray-100 mt-0.5"
      >
        <b-input-group
          prepend="+62"
          size="sm"
        >
          <b-form-input
            ref="inputNewPhone"
            :value="newPhoneNumber"
            type="text"
            placeholder="85128889129"
            maxlength="13"
            @input="onInputPhoneNumber"
            @keydown.enter.prevent="submitNewNumber"
            @keypress="onlyNumber"
          />
          <template #append>
            <b-button
              variant="primary"
              size="sm"
              :disabled="isSubmittingNewNumber || !newPhoneNumber"
              @click.stop="submitNewNumber"
            >
              <b-spinner
                v-if="isSubmittingNewNumber"
                small
                class="mr-1"
              />
              Tambah
            </b-button>
          </template>
        </b-input-group>
      </div>
    </div>

    <!-- Modal Konfirmasi Hapus Nomor CS AI -->
    <ModalDeletePhoneNumber
      :visible="showDeleteModal"
      :item-to-delete="itemToDelete"
      @deleted="onNumberDeleted"
      @close="closeDeleteModal"
    />
  </div>
</template>

<script>
import apiGetWhatsAppList, {
  apiGetFullWhatsAppList,
} from './service/api.whatsApp.service'
import {
  cleanPhoneInput,
  handlePhoneKeypress,
  validatePhoneInput,
  registerNewWhatsAppNumber,
} from './helper/whatsAppPhoneHelper'
import ModalDeletePhoneNumber from './ModalDeletePhoneNumber.vue'

export default {
  name: 'PhoneNumberSelect',
  components: {
    ModalDeletePhoneNumber,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      numberOptions: [],
      totalConnectedCount: 0,
      isDropdownOpen: false,
      isAddingNumber: false,
      newPhoneNumber: '',
      isSubmittingNewNumber: false,
      showDeleteModal: false,
      itemToDelete: null,
    }
  },
  computed: {
    canAddNumber() {
      const storeCount = this.$store?.state?.komchat?.whatsAppList
        ? this.$store.state.komchat.whatsAppList.filter(item => item.is_scan !== false).length
        : 0
      return this.totalConnectedCount < 3 && this.numberOptions.length < 3 && storeCount < 3
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.getListNumber()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(e) {
      if (this.$refs.phoneDropdownRef && !this.$refs.phoneDropdownRef.contains(e.target)) {
        this.isDropdownOpen = false
        this.isAddingNumber = false
      }
    },
    async getListNumber() {
      try {
        const [agentRes, fullRes] = await Promise.allSettled([
          apiGetWhatsAppList(),
          apiGetFullWhatsAppList(),
        ])

        const agentList = agentRes.status === 'fulfilled' ? agentRes.value?.data?.data || [] : []
        const fullList = fullRes.status === 'fulfilled' ? fullRes.value?.data?.data || [] : []

        if (agentList.length > 0) {
          this.numberOptions = agentList.map(item => {
            const phone = typeof item === 'object' ? item.phone_number : item
            const matched = fullList.find(
              f => this.formatRawPhone(f.phone_number) === this.formatRawPhone(phone),
            )
            if (typeof item === 'object' && item !== null) {
              return {
                ...item,
                id: item.id || matched?.id,
              }
            }
            return {
              id: matched?.id,
              phone_number: item,
            }
          })
        } else {
          this.numberOptions = fullList
        }

        const activeFull = fullList.filter(item => item.is_scan !== false)
        this.totalConnectedCount = Math.max(activeFull.length, this.numberOptions.length)

        // If value was provided or newly added, verify selection
        if (this.value && this.numberOptions.length > 0) {
          const matched = this.numberOptions.find(
            i => this.isSelected(i),
          )
          if (matched) {
            this.$emit('input', matched.phone_number || matched)
          }
        }
      } catch {
        try {
          const fullRes = await apiGetFullWhatsAppList()
          this.numberOptions = fullRes.data?.data || []
          this.totalConnectedCount = this.numberOptions.length
        } catch {
          if (this.$store?.state?.komchat?.whatsAppList?.length) {
            this.numberOptions = this.$store.state.komchat.whatsAppList
            this.totalConnectedCount = this.numberOptions.length
          }
        }
      }
    },
    formatDisplayPhone(phone) {
      if (!phone) return ''
      let raw = String(phone).trim()
      if (raw.startsWith('0')) {
        raw = `+62${raw.slice(1)}`
      } else if (raw.startsWith('62')) {
        raw = `+${raw}`
      } else if (!raw.startsWith('+62')) {
        raw = `+62${raw}`
      }

      const digits = raw.replace(/\D/g, '')
      if (digits.length >= 10) {
        const p1 = digits.slice(0, 5)
        const p2 = digits.slice(5, 9)
        const p3 = digits.slice(9)
        return `+${p1.slice(0, 2)}${p1.slice(2)} ${p2} ${p3}`
      }
      return raw
    },
    formatRawPhone(phone) {
      if (!phone) return ''
      const raw = String(phone).trim()
      if (raw.startsWith('0')) {
        return `+62${raw.slice(1)}`
      }
      if (raw.startsWith('62')) {
        return `+${raw}`
      }
      if (!raw.startsWith('+62')) {
        return `+62${raw}`
      }
      return raw
    },
    isSelected(item) {
      const current = this.value
      if (!current) return false
      const itemPhone = typeof item === 'object' ? item.phone_number : item
      return (
        String(current).trim() === String(itemPhone).trim()
        || this.formatRawPhone(current) === this.formatRawPhone(itemPhone)
      )
    },
    onSelectPhoneNumber(item) {
      const phoneVal = typeof item === 'object' ? item.phone_number : item
      this.$emit('input', phoneVal)
      this.isDropdownOpen = false
      this.isAddingNumber = false
    },
    openAddNumber() {
      if (!this.canAddNumber) {
        this.$toast_error({ message: 'Maksimal 3 nomor terhubung' })
        return
      }
      this.isAddingNumber = true
      this.$nextTick(() => {
        const el = this.$refs.inputNewPhone?.$el || this.$refs.inputNewPhone
        if (el?.focus) el.focus()
      })
    },
    openDeleteModal(item) {
      this.isDropdownOpen = false
      this.itemToDelete = item
      this.showDeleteModal = true
    },
    closeDeleteModal() {
      this.showDeleteModal = false
      this.itemToDelete = null
    },
    async onNumberDeleted(deletedItem) {
      const phoneVal = typeof deletedItem === 'object' ? deletedItem.phone_number : deletedItem
      if (
        this.value === phoneVal
        || this.formatRawPhone(this.value) === this.formatRawPhone(phoneVal)
      ) {
        this.$emit('input', '')
      }
      await this.getListNumber()
    },
    onInputPhoneNumber(val) {
      this.newPhoneNumber = cleanPhoneInput(val)
      this.$nextTick(() => {
        const el = this.$refs.inputNewPhone?.$el || this.$refs.inputNewPhone
        if (el) el.value = this.newPhoneNumber
      })
    },
    onlyNumber(event) {
      handlePhoneKeypress(event)
    },
    async submitNewNumber() {
      if (!this.canAddNumber) {
        this.$toast_error({ message: 'Maksimal 3 nomor terhubung' })
        return
      }

      const validation = validatePhoneInput(this.newPhoneNumber)
      if (!validation.isValid) {
        this.$toast_error({ message: validation.message })
        return
      }

      this.isSubmittingNewNumber = true
      try {
        const partnerId = this.$store?.state?.dashboard?.profile?.partner_id
        const { createdNumber, fullPhone } = await registerNewWhatsAppNumber({
          cleanedPhone: this.newPhoneNumber,
          partnerId,
        })

        this.$toast_success({ message: 'Nomor berhasil ditambahkan, silakan scan QR Code' })
        this.$emit('input', fullPhone)
        this.newPhoneNumber = ''
        this.isAddingNumber = false
        this.isDropdownOpen = false

        // Redirect to QR scan page with returnRoute
        this.$router.push({
          name: 'dashboard-whatsapp',
          params: {
            number: createdNumber || { phone_number: fullPhone, type: 'komchat_ai' },
            type: 'komchat_ai',
            returnRoute: this.$route.name,
            returnParams: this.$route.params,
            isNewNumber: true,
          },
        })
      } catch (error) {
        const errorMsg = error.message || error.response?.data?.message || 'Gagal menambahkan nomor'
        this.$toast_error({ message: errorMsg })
      } finally {
        this.isSubmittingNewNumber = false
      }
    },
  },
}
</script>
